import { useRef } from "react";
import { Dialog, DialogPopup, DialogTitle } from "@salestrak/ui";

import { useAuth } from "#app/lib/auth-context";
import { readJWT } from "#app/lib/jwt";
import { AdminConsole } from "#app/components/admin/admin-console";

/**
 * The platform admin console as an overlay over whatever page the superuser
 * is on — the same window-tier dialog the App Center and custom fields use,
 * so closing it lands back where it was opened. `superuser` is a JWT claim on
 * the access token; a non-superuser asking to open this renders nothing, so
 * the emitter can be called from anywhere without its own gate.
 */
export function AdminDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { state: authState } = useAuth();
  // Where focus lands when the dialog opens — see the popup below.
  const popupRef = useRef<HTMLDivElement>(null);

  const isSuperuser = authState.accessToken
    ? readJWT(authState.accessToken).superuser === true
    : false;
  if (!isSuperuser) return null;

  return (
    // Not responsive: desktop admin work.
    <Dialog responsive={false} open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPopup
        size="window"
        // The manage-dialog room rests on the page fill: its nav pane lifts
        // above it and its tables read on the surface they read on everywhere.
        surface="background"
        // Focus the popup itself, not the first tabbable — Chrome paints a
        // :focus-visible ring for programmatic focus, which would box the
        // first nav row on open. Keyboard users still get the ring once they
        // Tab to it.
        initialFocus={popupRef}
        ref={popupRef}
        showCloseButton={false}
      >
        <DialogTitle visuallyHidden>SalesTrak Admin</DialogTitle>
        <AdminConsole onClose={onClose} />
      </DialogPopup>
    </Dialog>
  );
}
